import type { LoanImportRow, LoanImportResult } from '@app/shared';

// Batching here is about the HTTP request, not the database work — the server already
// batches its transactions (see IMPORT_CHUNK in api/src/functions/loans.ts), which is
// what made a 5,000-row import fast. What that does not change is that posting the whole
// extract is still ONE request through the Static Web Apps proxy to the Function App:
// it runs for as long as it runs, shows the user an indeterminate spinner throughout,
// and on a timeout reports failure without saying how many rows actually landed.
//
// Splitting the post fixes those three: each request is short enough to sit well inside
// any platform timeout, progress is a real count, and a failure carries the tally from
// the batches that already committed (see PartialImportError).
//
// 250 matches the server's own chunk size, so one request maps to one server-side
// transaction rather than straddling two. Chunks go one at a time on purpose: the server
// checks which loan numbers already exist per chunk, so overlapping requests could both
// miss the same duplicate — it now reports the resulting unique-index violation as
// skipped rather than failing, but sequential requests keep the counts honest.
export const IMPORT_CHUNK_SIZE = 250;

/**
 * Where a batch sits in the file, passed to `send` so the request can say so.
 *
 * Without this every batch of one file opens its own tracked import server-side, and a
 * 5,000-row extract shows up as twenty unrelated runs in the history.
 */
export interface BatchMeta {
  seq: number;
  batchCount: number;
}

export interface ImportProgress {
  /** Rows sent so far, across completed chunks. */
  done: number;
  total: number;
}

/**
 * Thrown when a chunk fails. Carries the tally from the chunks that already
 * succeeded — those rows are committed and stay committed, so the user needs to
 * see them rather than a bare failure.
 *
 * Re-running the same file is safe and is the intended recovery: the import is
 * insert-only and skips any umwsbLoanNumber already present, so a retry picks up
 * where this left off.
 */
export class PartialImportError extends Error {
  constructor(
    message: string,
    public partial: LoanImportResult,
    public failedAtRow: number,
    public override cause: unknown,
  ) {
    super(message);
    this.name = 'PartialImportError';
  }
}

export function chunk<T>(rows: readonly T[], size: number): T[][] {
  if (size < 1) throw new Error('chunk size must be at least 1');
  const out: T[][] = [];
  for (let i = 0; i < rows.length; i += size) out.push(rows.slice(i, i + size));
  return out;
}

/**
 * Sends `rows` in sequential batches and folds the per-batch results into one.
 *
 * `send` is injected rather than imported so this stays testable without a fetch
 * stub — the real caller passes the api.ts client.
 */
export async function runChunkedImport(
  rows: readonly LoanImportRow[],
  send: (rows: LoanImportRow[], meta: BatchMeta) => Promise<LoanImportResult>,
  opts: { chunkSize?: number; onProgress?: (p: ImportProgress) => void } = {},
): Promise<LoanImportResult> {
  const { chunkSize = IMPORT_CHUNK_SIZE, onProgress } = opts;
  const total = rows.length;
  // `updated` is always 0 from the server (the import is insert-only) but is summed
  // rather than hardcoded so this doesn't silently lie if that ever changes.
  const acc: LoanImportResult = { inserted: 0, updated: 0, skipped: 0, errors: [] };

  let done = 0;
  onProgress?.({ done, total });

  const batches = chunk(rows, chunkSize);
  for (const [seq, batch] of batches.entries()) {
    let r: LoanImportResult;
    try {
      r = await send(batch, { seq, batchCount: batches.length });
    } catch (e) {
      throw new PartialImportError(
        `Import stopped after ${done} of ${total} rows: ${e instanceof Error ? e.message : String(e)}`,
        acc,
        done,
        e,
      );
    }
    acc.inserted += r.inserted;
    acc.updated += r.updated;
    acc.skipped += r.skipped;
    acc.errors.push(...r.errors);
    done += batch.length;
    onProgress?.({ done, total });
  }

  return acc;
}
